// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { Icon, type IconProps } from 'Components/frameworks/icon/Icon.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';

import { themes } from './Button.themes';

export type ButtonProps = {
  ariaLabel?: string | null,
  children?: Node,
  className?: string,
  color?: Colors,
  colorBg?: Colors,
  colorBorder?: Colors,
  dataSelector?: string,
  dataTestId?: string,
  // if button type is "submit",
  // then the form property might point
  // to the form that needs to submit,
  // in case the button is out of the form
  form?: string,
  id?: string,
  iconLeft?: IconProps,
  iconRight?: IconProps,
  isDisabled?: boolean,
  isExternalLink?: boolean,
  isFlat?: boolean,
  isLoading?: boolean,
  minWidth?: string,
  onBlur?: (SyntheticEvent<>) => void,
  onClick?: (SyntheticEvent<>) => mixed,
  onFocus?: (SyntheticEvent<>) => void,
  queryParamsToIgnore?: $ReadOnlyArray<string>,
  size?: 'small' | 'medium' | 'large',
  tabIndex?: number,
  target?: string,
  theme?: string,
  trackingData?: TrackingData,
  type?: 'button' | 'submit' | 'reset',
  url?: string,
  style?: CSSRuleList,
  dataCsOverrideId?: string,
};

const getRoleProps = ({
  url,
  target,
  type,
  isExternalLink,
  isAnchor,
  queryParamsToIgnore,
  form,
}) => {
  if (isExternalLink) return { href: url, target };
  if (isAnchor) return { url, target, queryParamsToIgnore };
  return { type, ...(form ? { form } : {}) };
};

export function Button(props: ButtonProps) {
  const {
    ariaLabel,
    children,
    className,
    dataSelector,
    dataTestId,
    form,
    iconLeft,
    iconRight,
    id,
    isDisabled,
    isExternalLink,
    isLoading,
    onBlur,
    onClick,
    onFocus,
    queryParamsToIgnore,
    target,
    theme = 'default',
    trackingData,
    type = 'button',
    url,
    style,
    tabIndex = 0,
    dataCsOverrideId,
  } = props;

  if (!Children.count(children)) return null;

  const isAnchor = !!url;
  const Comp = isAnchor ? DynamicLink : 'button';

  const { thButton, thChildren, thContent, thIcon, thSize, thSpinner } = themes[theme](props);

  return (
    <TrackClick
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      as={isExternalLink ? 'a' : Comp}
      className={classnames(className, thButton)}
      dataSelector={dataSelector}
      data-testid={dataTestId}
      disabled={isDisabled}
      id={id}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      rel={isExternalLink ? 'nofollow' : undefined}
      // $FlowFixMe
      {...getRoleProps({
        url,
        target,
        type,
        isExternalLink,
        isAnchor,
        queryParamsToIgnore,
        ...(form ? { form } : {}),
      })}
      style={style}
      tabIndex={tabIndex}
      trackingData={trackingData}
      data-cs-override-id={dataCsOverrideId}
    >
      <span className={thContent} style={{ ...thSize }}>
        {!isEmpty(iconLeft) && <Icon className={thIcon} hasRtl width="1em" {...iconLeft} />}
        <span className={thChildren}>{children}</span>
        {!isEmpty(iconRight) && <Icon className={thIcon} hasRtl width="1em" {...iconRight} />}
        {isLoading && !isDisabled && <Spinner className={thSpinner} color="current" size="small" />}
      </span>
    </TrackClick>
  );
}
